/**
 * ModalPopUp component that displays a popup dialog with subscription content
 * after specific user interactions or timeouts.
 * @component
 * @returns {JSX.Element} The rendered ModalPopUp component.
 */

import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import CrossIcon from "../../../assets/svg/CrossIcon";
import MaticLogoBlack from "../../../assets/svg/MaticLogoBlack";
import getEntries from "@/utils/ContentfulServices";
import SubscribeSection from "./SubscribeSection";
import SubscribeSectionSocial from "./SubscribeSectionSocial";
import { useRouter } from "next/router";
import Image from "next/image";

const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;

export default function ModalPopUp() {
  const [isOpen, setIsOpen] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const timerId = useRef();

  const router = useRouter();
  const { pathname } = router;

  const options = [
    'ANDROID SUPPORT',
    'DEMO SIGNUPS', 
    'INTERNATIONAL SHIPPING',
    'LEARN MORE ABOUT MATIC'
  ];

  const handleOptionClick = (option) => {
    setSelectedOptions(prev => {
      if (prev.includes(option)) {
        return prev.filter(item => item !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  const cleanUp = () => {
    if (timerId.current) clearTimeout(timerId.current);

    setIsTimeout(false);
    setIsScrolled(false);
  };

  const dontShowModal = ["/order-confirm", "/cart"].includes(pathname);

  const openPopup = () => {
    const isAnyModalOpen = document.querySelectorAll('[role="dialog"]');

    if (isAnyModalOpen?.length > 0) {
      cleanUp();
      return;
    }

    if (dontShowModal) {
      cleanUp();
      return;
    }

    localStorage.setItem("lastPopupTime", Date.now().toString());

    setIsOpen(true);
  };

  const handlePopup = () => {
    const now = Date.now();
    const isSubscribed = localStorage.getItem("isSubscribed") === "true";
    const lastPopupTime = localStorage.getItem("lastPopupTime");

    const hasPassedTwoDays =
      lastPopupTime && now - parseInt(lastPopupTime) >= TWO_DAYS_IN_MS;

    // Show the popup if:
    // 1. More than two days have passed since the last popup time and the user is not subscribed, or
    // 2. lastPopupTime is not available (undefined or null) and the user is not subscribed
    if ((hasPassedTwoDays || !lastPopupTime) && !isSubscribed) {
      openPopup();
    } else {
      cleanUp();
    }
  };

  useEffect(() => {
    if (isTimeout || isScrolled) {
      handlePopup();
    }
  }, [isTimeout, isScrolled, pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const totalScrollHeight = document.documentElement.scrollHeight;
      if (scrollY > totalScrollHeight / 2) {
        setIsScrolled(true);
        window.removeEventListener("scroll", handleScroll);
        if (timerId.current) clearTimeout(timerId.current);
      }
    };

    timerId.current = setTimeout(() => {
      if (timerId.current) clearTimeout(timerId.current);
      setIsTimeout(true);
    }, 15000);

    window.addEventListener("scroll", handleScroll);

    // remove event listener
    return () => {
      if (timerId.current) clearTimeout(timerId.current);
      window.removeEventListener("scroll", handleScroll);
      setIsOpen(false);
    };
  }, [pathname]);

  return (
    <>
      {!dontShowModal && (
        <Dialog open={isOpen} onClose={() => {}} className="relative outline-0">
          <div className="fixed inset-0 bg-primary50 z-[3]" />
          <div className="fixed z-10 inset-0 overflow-y-auto outline-0">
            <div className="flex items-center justify-center min-h-screen">
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

              <div className="relative bg-white rounded-[20px] mx-auto p-6 md:py-10 md:pr-10 md:pl-0">
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-[8px] right-[8px] text-gray-500 hover:text-gray-700"
                >
                  <CrossIcon classes="w-[26px] h-[26px]" strokeSize="1" />
                </button>

                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2 flex justify-center ml-[40px] mr-[20px] hidden md:block">
                          <Image
                      src="https://images.ctfassets.net/6p328xbjc78k/5Dcicw4Wc8wbTSfBmIY2f3/72d5a968cfa693eac0eb146e979f71b8/66bb3396861fa873c8560ab686ed4854e20eb0f6_1_.jpg"
                      alt="Subscribe Pop Up"
                      className="object-cover w-full h-full rounded-[15px]"
                      width={300}
                      height={300}
                          />
                  </div>

                  <div className="md:w-1/2 text-center flex items-center justify-center flex-col mt-[30px] lg:mt-0">
                    <h3 className="text-[36px]">Get the latest from Matic!</h3>
                    <h6 className="text-[24px] mt-[7px]">
                      Join our mailing list for updates from Mountain View.
                    </h6>
                    <h6 className="text-[18px] mt-[7px]">
                      What would you like to hear about?
                    </h6>

                    <div className="w-full px-[60px] mt-[26px] flex flex-col gap-[10px]">
                      {options.map((option) => {
                        const isSelected = selectedOptions.includes(option);
                        return (
                          <div 
                            key={option}
                            onClick={() => handleOptionClick(option)}
                            className={`border rounded-full py-[16px] w-full cursor-pointer transition-colors duration-200 ${
                              isSelected
                                ? 'bg-primary border-primary'
                                : 'border-black bg-white hover:bg-gray-100'
                            }`}
                          >
                            <h6 className={`font-trimMono md:text-[18px] text-[14px] font-medium ${
                              isSelected ? 'text-white' : 'text-black'
                            }`}>
                              {option}
                            </h6>
                          </div>
                        );
                      })}
                    </div>

                    <SubscribeSection 
                      onCloseHandler={() => setIsOpen(false)} 
                      selectedOptions={selectedOptions}
                    />
                    <SubscribeSectionSocial />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
